import {
  createContext,
  use,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useQuery } from "@tanstack/react-query";

import { getConfig } from "lib/get-config";
import {
  getCachedLayout,
  getCacheKey,
  setCachedLayout,
} from "lib/layout/layout-cache";
import { perfEnd, perfLog } from "lib/perf/perf";
import { SettingsContext } from "lib/settings/use-settings";
import type { VirtualLayout } from "lib/types";
import { useWindowSize } from "lib/utils/viewport";

export const BUFFER_SIZE = 1; // Можно поставить Infinity, чтобы отрисовать всю книгу без виртуализации

const ENABLE_LAYOUT_CACHE = false;

interface ContentLayoutContextType {
  layout: VirtualLayout | null;
  setLayout: (
    layout: VirtualLayout | ((prev: VirtualLayout | null) => VirtualLayout)
  ) => void;
  layoutKey: string;
  totalPages: number;
  isResizingRef: RefObject<boolean>;
  isPending: boolean;
  measurementProgress: { measured: number; total: number } | null;
  setMeasurementProgress: (
    progress: { measured: number; total: number } | null
  ) => void;
}

export const ContentLayoutContext = createContext<
  ContentLayoutContextType | undefined
>(undefined);
ContentLayoutContext.displayName = "ContentLayoutContext";

export function ContentLayoutProvider({ children }: { children: ReactNode }) {
  const settings = use(SettingsContext)!;
  const { art } = getConfig();

  const { windowSize, isResizing } = useWindowSize();
  const isResizingRef = useRef(false);

  function getLayoutKey(size: string) {
    return `${settings.font}-${settings.fontSize}-${settings.margins}-${settings.lineHeight}-${settings.cols}-${settings.layoutDirection}-${settings.textAlignJustify}-${settings.wordWrap}-${size}`;
  }

  const layoutKey = getLayoutKey(windowSize);

  const cacheKey = ENABLE_LAYOUT_CACHE ? getCacheKey(art, layoutKey) : null;

  const { data: cachedEntry } = useQuery({
    queryKey: ["layout-cache", cacheKey],
    queryFn: async () => {
      const layout = await getCachedLayout(cacheKey!);
      return layout ? { layout, forCacheKey: cacheKey! } : null;
    },
    enabled: !!cacheKey,
  });
  const cachedLayout =
    cachedEntry?.forCacheKey === cacheKey ? cachedEntry.layout : null;

  const [layouts, setLayouts] = useState<Map<string, VirtualLayout>>(() => {
    if (cachedLayout) {
      const map = new Map<string, VirtualLayout>();
      map.set(layoutKey, cachedLayout);
      return map;
    }
    return new Map();
  });

  const cachedFallback = cachedLayout?.mode === "virtual" ? cachedLayout : null;
  const layout = layouts.get(layoutKey) ?? cachedFallback;

  const isPending = isResizing || layout === null;

  perfLog("render", "ContentLayoutProvider", {
    key: layoutKey.slice(-20),
    mode: layout?.mode ?? "null",
    isPending,
    cached: !!cachedFallback,
  });

  function setLayout(
    newLayout: VirtualLayout | ((prev: VirtualLayout | null) => VirtualLayout)
  ) {
    setLayouts(prev => {
      const existingLayout = prev.get(layoutKey);
      if (existingLayout?.mode === "virtual") return prev;

      const updated = new Map(prev);
      const layout =
        typeof newLayout === "function"
          ? newLayout(existingLayout ?? null)
          : newLayout;
      updated.set(layoutKey, layout);

      if (layout.mode === "virtual") {
        perfEnd("resize:cycle");
      }

      if (ENABLE_LAYOUT_CACHE && layout.mode === "virtual") {
        const cacheKey = getCacheKey(art, layoutKey);
        setCachedLayout(cacheKey, layout);
      }

      return updated;
    });
  }

  const totalPages =
    layout && layout.contentWidth && layout.columnWidth > 0
      ? layout.totalPages
      : 0;

  const [measurementProgress, setMeasurementProgress] = useState<{
    measured: number;
    total: number;
  } | null>(null);

  return (
    <ContentLayoutContext
      value={{
        layout,
        setLayout,
        layoutKey,
        totalPages,
        isResizingRef,
        isPending,
        measurementProgress,
        setMeasurementProgress,
      }}
    >
      {children}
    </ContentLayoutContext>
  );
}
